import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEmployeeInformation,
  updateBankField,
  setBankImage,
  removeBankImage,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./BankDetails.module.scss";

const BankDetails = forwardRef(function BankDetails(_, ref) {
  const dispatch = useDispatch();

  const { bankDetails, bankImages } = useSelector(selectEmployeeInformation);

  const [errorFields, setErrorFields] = useState(new Set());

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const updateField = (name, value) => {
    dispatch(updateBankField({ name, value }));
    clearError(name);
  };

  const updateNumberField = (name, value) => {
    updateField(name, value.replace(/\D/g, ""));
  };

  const handleImageChange = (file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;

    if (bankImages.document?.url) {
      URL.revokeObjectURL(bankImages.document.url);
    }

    dispatch(
      setBankImage({
        name: "document",
        image: {
          name: file.name,
          file,
          url: URL.createObjectURL(file),
        },
      }),
    );

    clearError("document");
  };

  const removeImage = () => {
    if (bankImages.document?.url) {
      URL.revokeObjectURL(bankImages.document.url);
    }

    dispatch(removeBankImage("document"));
    clearError("document");
  };

  const getMissingFields = useCallback(() => {
    const missing = [];
    const missingNames = new Set();

    if (!bankImages.document && !bankDetails.bank) {
      missing.push("Bank Document");
      missingNames.add("document");
    }

    if (!String(bankDetails.account_holder_name || "").trim()) {
      missing.push("Account Holder Name");
      missingNames.add("account_holder_name");
    }

    if (!String(bankDetails.bank_name || "").trim()) {
      missing.push("Bank Name");
      missingNames.add("bank_name");
    }

    if (!String(bankDetails.bank_branch || "").trim()) {
      missing.push("Bank Branch");
      missingNames.add("bank_branch");
    }

    if (!String(bankDetails.ifsc_code || "").trim()) {
      missing.push("IFSC Code");
      missingNames.add("ifsc_code");
    }

    if (!String(bankDetails.account_number || "").trim()) {
      missing.push("Account Number");
      missingNames.add("account_number");
    }

    setErrorFields(missingNames);

    return missing;
  }, [bankDetails, bankImages]);

  useImperativeHandle(ref, () => ({
    getMissingFields,
    isComplete: () => getMissingFields().length === 0,
  }));

  return (
    <div className={styles.bankDetails}>
      <header className={styles.sectionHeader}>Bank Details</header>

      <div className={styles.content}>
        <p className={styles.note}>
          Note: Upload the cheque leaf or passbook image. Supported formats:
          JPG, JPEG, PNG. Maximum size: 10 MB.
        </p>

        {/* Document Type */}
        <div className={styles.docType}>
          <label>
            <input
              type="checkbox"
              checked={bankDetails.documentType === "cheque"}
              onChange={() => updateField("documentType", "cheque")}
            />
            Cheque Leaf
          </label>

          <label>
            <input
              type="checkbox"
              checked={bankDetails.documentType === "passbook"}
              onChange={() => updateField("documentType", "passbook")}
            />
            Passbook
          </label>
        </div>

        {/* Upload */}
        <div className={styles.uploadBlock}>
          <div className={styles.previewRow}>
            {bankImages.document ? (
              <>
                <img
                  src={bankImages.document.url}
                  className={styles.imagePreview}
                  alt="Bank document preview"
                />
                <button
                  className={styles.removeButton}
                  onClick={removeImage}
                  type="button"
                >
                  Remove Image
                </button>
              </>
            ) : bankDetails.bank ? (
              <>
                <img
                  src={bankDetails.bank}
                  className={styles.imagePreview}
                  alt="Bank document preview"
                />
                <button
                  className={styles.removeButton}
                  onClick={removeImage}
                  type="button"
                >
                  Remove Image
                </button>
              </>
            ) : (
              <label className={styles.uploadButton}>
                Upload Image
                <input
                  className={styles.hiddenInput}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={(e) => handleImageChange(e.target.files?.[0])}
                />
              </label>
            )}
          </div>

          {errorFields.has("document") && (
            <p className={styles.fieldError}>
              Please upload a cheque leaf or passbook image
            </p>
          )}
        </div>

        {/* Form */}
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>
              Account Holder Name<b>*</b>
            </span>
            <input
              value={bankDetails.account_holder_name || ""}
              className={
                errorFields.has("account_holder_name") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField("account_holder_name", e.target.value.toUpperCase())
              }
            />
            {errorFields.has("account_holder_name") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Bank Name<b>*</b>
            </span>
            <input
              value={bankDetails.bank_name || ""}
              className={errorFields.has("bank_name") ? styles.inputError : ""}
              onChange={(e) =>
                updateField("bank_name", e.target.value.toUpperCase())
              }
            />
            {errorFields.has("bank_name") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Bank Branch<b>*</b>
            </span>
            <input
              value={bankDetails.bank_branch || ""}
              className={
                errorFields.has("bank_branch") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField("bank_branch", e.target.value.toUpperCase())
              }
            />
            {errorFields.has("bank_branch") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              IFSC Code<b>*</b>
            </span>
            <input
              value={bankDetails.ifsc_code || ""}
              className={errorFields.has("ifsc_code") ? styles.inputError : ""}
              onChange={(e) =>
                updateField("ifsc_code", e.target.value.toUpperCase())
              }
            />
            {errorFields.has("ifsc_code") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Account Number<b>*</b>
            </span>
            <input
              value={bankDetails.account_number || ""}
              inputMode="numeric"
              className={
                errorFields.has("account_number") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateNumberField("account_number", e.target.value)
              }
            />
            {errorFields.has("account_number") && (
              <p className={styles.fieldError}>Please fill this field</p>
            )}
          </label>

          <label className={styles.field}>
            <span>Phone Number</span>
            <input
              value={bankDetails.phone_number || ""}
              inputMode="numeric"
              onChange={(e) =>
                updateNumberField("phone_number", e.target.value)
              }
            />
          </label>

          <label className={styles.field}>
            <span>Remarks</span>
            <input
              value={bankDetails.remarks || ""}
              onChange={(e) => updateField("remarks", e.target.value)}
            />
          </label>
        </div>
      </div>
    </div>
  );
});

export default BankDetails;
